import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Loader2, AlertCircle } from "lucide-react";
import apiService from '@/services/api';

export default function LoginPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    const email = e.target.email.value;
    const password = e.target.password.value;
    
    try {
      // Authenticate user via backend API
      const response = await apiService.login(email, password);
      
      if (response.user) {
        // Store user info in localStorage
        localStorage.setItem('currentUser', JSON.stringify(response.user));
        setIsLoading(false);
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.message || 'Invalid email or password. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Side - Cultural Image */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-red-900 to-pink-800">
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/20 to-transparent"></div>
        <img 
          src="/ambae-red-mat.jpg" 
          alt="Authentic Ambae Red Mat - Traditional Vanuatu weaving" 
          className="absolute inset-0 w-full h-full object-cover object-center"
          style={{ imageRendering: 'high-quality' }}
        />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <h1 className="text-5xl font-bold mb-2 tracking-tight">PENAMA</h1>
          <h2 className="text-3xl font-semibold mb-4 text-blue-100">Education Portal</h2>
          <p className="text-lg text-blue-200">Empowering Education in Vanuatu</p>
          <div className="mt-8 space-y-3">
            <div className="flex items-center gap-3">
              <div className="h-2 w-2 rounded-full bg-white"></div>
              <span className="text-sm">Manage Curriculum & Assessments</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-2 w-2 rounded-full bg-white"></div>
              <span className="text-sm">Track Student Progress</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-2 w-2 rounded-full bg-white"></div>
              <span className="text-sm">Real Student Data from 343 Students</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-4 lg:p-8">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-8">
            <div className="flex items-center gap-3 font-bold text-2xl tracking-tight text-primary mb-4">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center text-primary-foreground text-xl shadow-lg">
                P
              </div>
              <div>
                <div className="text-2xl leading-tight">Penama Education Portal</div>
                <div className="text-xs font-normal text-muted-foreground">Penama Province, Vanuatu</div>
              </div>
            </div>
            
            {/* Penama Education Portal Logo */}
            <div className="flex flex-col items-center gap-3">
              <div className="relative flex items-center justify-center">
                {/* Logo Image with enhanced styling */}
                <div className="relative group">
                  <div className="absolute -inset-2 bg-gradient-to-r from-blue-600 to-primary rounded-full opacity-20 group-hover:opacity-30 blur transition-opacity"></div>
                  <img 
                    src="/penama-logo.png" 
                    alt="Penama Education Portal Logo" 
                    className="relative w-44 h-44 object-contain drop-shadow-2xl"
                  />
                </div>
              </div>
              <div className="text-center space-y-2">
                <h3 className="text-2xl font-bold text-primary tracking-wide">PENAMA EDUCATION PORTAL</h3>
                <p className="text-sm text-muted-foreground italic">Empowering Education Across Penama Province</p>
                <p className="text-xs font-semibold text-muted-foreground mt-2 px-4 py-1 bg-primary/10 rounded-full inline-block">
                  Multi-School System
                </p>
              </div>
            </div>
          </div>
          
          <Card className="border-2 shadow-xl">
          <CardHeader className="space-y-1 text-center">
            <CardTitle className="text-2xl font-bold">Welcome back</CardTitle>
            <CardDescription>
              Enter your credentials to access your classroom
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input 
                  id="email"
                  name="email"
                  type="email" 
                  placeholder="teacher@school.edu" 
                  defaultValue="roytari@spc.edu.vu"
                  required 
                  className="bg-muted/30"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link to="#" className="text-sm font-medium text-primary hover:underline">
                    Forgot password?
                  </Link>
                </div>
                <Input 
                  id="password"
                  name="password"
                  type="password" 
                  required 
                  className="bg-muted/30"
                  defaultValue="Teacher@2024"
                />
              </div>
              <Button className="w-full h-11 font-medium" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs font-semibold text-blue-900 mb-2">Available Accounts:</p>
              <div className="space-y-1 text-xs text-blue-800">
                <p>• <strong>Principal:</strong> principal@spc.edu.vu (Principal@2024)</p>
                <p>• <strong>Roy Tari (ICT):</strong> roytari@spc.edu.vu (Teacher@2024)</p>
                <p>• <strong>Coordinator:</strong> secondary.coordinator@penama.edu.vu</p>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4 text-center text-sm text-muted-foreground">
            <div className="relative w-full">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">
                  Or continue with
                </span>
              </div>
            </div>
            <Button variant="outline" className="w-full h-11">
              <svg className="mr-2 h-4 w-4" aria-hidden="true" focusable="false" data-prefix="fab" data-icon="google" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488 512"><path fill="currentColor" d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"></path></svg>
              Google Workspace
            </Button>
            <p>
              Don't have an account?{' '}
              <Link to="#" className="font-medium text-primary hover:underline">
                Contact Admin
              </Link>
            </p>
          </CardFooter>
        </Card>
        </div>
      </div>
    </div>
  );
}
